import type { PlatformPos } from "@/platform/types";
import { codigoDelError, estadoDelError } from "./errores";
import { datosDeCore } from "./respuestaDeCore";

/**
 * El PIN del cajero (trd-client#346 AC-2; §2.6 de OFFLINE-POS-RONDA-2.md).
 * No sale del equipo: core registra que ese usuario es cajero de la terminal,
 * y el equipo guarda el hash. Se carga con red y con la sesión del propio
 * cajero, así se sabe que es él.
 */

/** Un PIN es de 4 a 8 números. `null` si está bien. */
export function problemaDelFormato(pin: string): string | null {
  return /^\d{4,8}$/.test(pin)
    ? null
    : "El PIN tiene que tener de 4 a 8 números.";
}

/** Al cargarlo, además, se repite igual. */
export function problemaDelPin(pin: string, repetido: string): string | null {
  return (
    problemaDelFormato(pin) ??
    (pin !== repetido ? "Los dos PIN no coinciden." : null)
  );
}

export type ResultadoDelPin =
  | { tipo: "ok" }
  | { tipo: "sin-respuesta" }
  | { tipo: "rechazado"; mensaje: string };

export interface DependenciasDelPin {
  pos: Pick<PlatformPos, "setPin">;
  post: (url: string, body: unknown) => Promise<unknown>;
}

export const MENSAJE_SIN_IDENTIDAD =
  "Tu usuario no tiene una identidad de TRUD con la que core pueda registrarte como cajero: pedile al dueño que te invite de nuevo.";

/**
 * El `userId` con que core registró al cajero: es el que el mandato trae en
 * `cashiers`, y el que el próximo latido usa para decidir si el PIN se queda.
 * Hoy coincide con el de la sesión; si algún día difieren, guardar el PIN con
 * el de la sesión lo borraría sin avisar. Sin él, el de la sesión.
 */
export function userIdRegistrado(
  respuesta: unknown,
  deLaSesion: string
): string {
  const cuerpo = (respuesta as { data?: unknown } | null | undefined)?.data;
  const cajero = datosDeCore(cuerpo) as { userId?: unknown } | null | undefined;
  return typeof cajero?.userId === "string" && cajero.userId.trim() !== ""
    ? cajero.userId
    : deLaSesion;
}

/**
 * Registra al usuario de la sesión como cajero de la terminal y, recién con
 * eso, guarda su PIN con el `userId` que devolvió core. Si el registro no
 * responde, no se guarda nada: sin registro, el PIN no serviría para vender.
 */
export async function cargarMiPin(
  { pos, post }: DependenciasDelPin,
  args: {
    businessId: string;
    terminalId: string;
    installationId: string;
    userId: string;
    nombre: string;
    pin: string;
  }
): Promise<ResultadoDelPin> {
  let registrado: unknown;
  try {
    registrado = await post(
      `/pos/terminals/${encodeURIComponent(args.terminalId)}/cashiers`,
      { installationId: args.installationId }
    );
  } catch (error) {
    const estado = estadoDelError(error);
    if (
      estado === undefined ||
      estado >= 500 ||
      estado === 408 ||
      estado === 429
    ) {
      return { tipo: "sin-respuesta" };
    }
    if (codigoDelError(error) === "CASHIER_IDENTITY_MISSING") {
      return { tipo: "rechazado", mensaje: MENSAJE_SIN_IDENTIDAD };
    }
    const data = (error as { response?: { data?: any } })?.response?.data;
    return {
      tipo: "rechazado",
      mensaje:
        data?.message ??
        data?.data?.message ??
        `El servidor no dejó cargar el PIN (HTTP ${estado}).`,
    };
  }
  await pos.setPin({
    businessId: args.businessId,
    userId: userIdRegistrado(registrado, args.userId),
    nombre: args.nombre,
    pin: args.pin,
  });
  return { tipo: "ok" };
}
